import { safeTgtLogger } from './safeTgtLogger'
import { getOtelUserAttributes } from './otelUserAttributes'
import type { MPMEnvType } from './env'
import {
    OpenTelemetryManager,
    resourceFromAttributes,
} from '@measurement/opentelemetry-web'

export const initializeOpenTelemetry = async (config: MPMEnvType) => {
    if (process.env.REACT_APP_E2E === 'true') {
        return
    }

    OpenTelemetryManager.initialize({
        apiKey: config.otel?.apiKey || '',
        serviceName: 'mpmui',
        globalLogLevel: 'INFO',
        attributesFn: getOtelUserAttributes,
        resource: resourceFromAttributes({
            'labels.environment': config.environment,
            'service.version': process.env.APP_VERSION ?? '0.0.0',
        }),
        urlTemplates: [
            '/bi_reporting_assets/v3/execute_cards',
            '/fulfillment_api/v1',
            '/location_proximities/v1/nearby_locations',
            '/mpm_aggregations/v1/assets_protection/payroll',
            '/mpm_aggregations/v1/assets_protection/secure',
            '/mpm_aggregations/v1/assets_protection/theft',
            '/mpm_aggregations/v1/brands/trends/',
            '/mpm_aggregations/v1/fulfillment_capabilities/{location_id}',
            '/mpm_aggregations/v1/fulfillment_capabilities/non_stores/',
            '/mpm_aggregations/v1/greenfield/cards/{card_id}',
            '/mpm_aggregations/v1/inbound_trailers/rgd_trailers',
            '/mpm_aggregations/v1/inbound_trailers/store_trailers',
            '/mpm_aggregations/v1/inbound_trailers/store_trailers/eta_events',
            '/mpm_aggregations/v1/inbound_trailers/trailer_schedules',
            '/mpm_aggregations/v1/item_details',
            '/mpm_aggregations/v1/my_target_performance/stores',
            '/mpm_aggregations/v1/location_hierarchy',
            '/mpm_aggregations/v1/payroll/overtime',
            '/mpm_aggregations/v1/payroll/workgroup',
            '/mpm_aggregations/v1/payroll/workgroup/WORKGROUP/{workgroup_name}',
            '/mpm_aggregations/v1/redcards/counts',
            '/mpm_aggregations/v1/safety/leading/',
            '/mpm_aggregations/v1/safety/leading/{location_type}/{location_id}',
            '/mpm_aggregations/v1/safety/store/',
            '/mpm_aggregations/v1/safety/total/',
            '/mpm_aggregations/v1/safety/total/{location_type}/{location_id}',
            '/mpm_aggregations/v1/sales/merchandise',
            '/mpm_aggregations/v1/sales/merchandise/groups/{group_name}',
            '/mpm_aggregations/v1/sales/merchandise/groups/{group_name}/divisions/{division_name}',
            '/mpm_aggregations/v1/sales/merchandise/groups/{group_name}/divisions/{division_name}/departments/{department_name}',
            '/mpm_aggregations/v1/sales/work_areas',
            '/mpm_aggregations/v1/sales/work_areas/groups/{group_name}',
            '/mpm_aggregations/v1/sales/work_areas/groups/{group_name}/' +
                'centers/{center_name}/divisions/{division_name}/' +
                'departments/{department_name}',
            '/mpm_aggregations/v1/shortage/',
            '/mpm_aggregations/v1/shortage/{department_name}',
            '/mpm_aggregations/v1/shortage/merchandise/',
            '/mpm_aggregations/v1/shortage/merchandise/groups/{group_name}',
            '/mpm_aggregations/v1/shortage/merchandise/groups/{group_name}/divisions/{division_name}',
            '/mpm_aggregations/v1/shortage/merchandise/groups/{group_name}/divisions/{division_name}/departments/{department_name}',
            '/mpm_aggregations/v1/user_contexts/',
            '/mpm_aggregations/v1/user_contexts/{context_id}',
            '/mpm_brand_sales/v2/brand_sales/',
            '/mpm_brand_sales/v2/brands/',
            '/mpm_digital_notes/v2/notes/items',
            '/mpm_feedbacks/v1/',
            '/mpm_fulfillment_analytics/v1/districts',
            '/mpm_fulfillment_analytics/v1/groups',
            '/mpm_fulfillment_analytics/v1/ndd_metrics',
            '/mpm_fulfillment_analytics/v1/ndd_metrics/districts',
            '/mpm_fulfillment_analytics/v1/ndd_metrics/groups',
            '/mpm_fulfillment_analytics/v1/ndd_metrics/locations',
            '/mpm_fulfillment_analytics/v1/ndd_metrics/regions',
            '/mpm_fulfillment_analytics/v1/regions',
            '/mpm_fulfillment_analytics/v1/stores',
            '/mpm_team_members/v2/users',
            '/mpm_ui_data/v1/dimensions',
            '/mpm_ui_data/v1/performance_metrics',
            '/mpm_ui_data/v1/store-recalls',
            '/redcard_allocation_statistics/v1/non_stores',
            '/redcard_allocation_statistics/v1/stores',
            '/store_briefings/v1/locations/{location_id}/store_briefing',
            '/store_leaders/v1/storefacts',
            '/store_sales/v2/stores',
            'v1/projects/tgt-legato-firebase/namespaces/firebase:fetch',
            '/.well-known/openid-configuration',
            '/openid/connect/jwks.json',
        ],
    })

    safeTgtLogger.info('OpenTelemetry initialized at app start', {
        event: {
            action: 'otel_init_complete',
            category: 'app_lifecycle',
            outcome: 'success',
        },
    })

    // NOTE on unhandled promise rejections:
    // zone.js (bundled with @measurement/opentelemetry-web) replaces the
    // global Promise and fully owns the rejection pipeline. It logs
    // "Unhandled Promise rejection:" via console.error, which the TAP
    // logging sidecar captures. Neither window.onunhandledrejection nor
    // addEventListener('unhandledrejection') are invoked by zone.js, so
    // custom handlers here would be dead code. To reduce these TAP entries,
    // we prevent rejections at their source (see getNearbyLocationsQuery,
    // QueryClient defaults in App.tsx, and useRefresh).
}
